import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'

/** The OAuth request as the API put it on `/oauth/authorize`: passed along untouched. */
export type AuthorizeParams = Record<string, string>

export interface AuthorizeInfo {
  client: { name: string; uri?: string | null; logoUri?: string | null }
  redirectHost?: string | null
  scopes: { key: string; label: string }[]
  moneyRequiresConfirmation?: boolean
  communities: { id: string; name: string; slug?: string; logoUrl?: string | null; domain?: string | null; role: string; mcpAllowed?: boolean }[]
}

export interface McpConnection {
  id: string
  clientName: string
  clientUri?: string | null
  scope?: string | null
  community?: { id: string; name: string; slug?: string } | null
  member?: { id: string; fullName: string } | null
  createdAt: string
  lastUsedAt?: string | null
}

/** F-40: the MCP consent screen and the assistants connected to a person or a community. */
export class McpOAuthResource {
  constructor(private ctx: SdkContext) {}

  async authorizeInfo(params: AuthorizeParams): Promise<AuthorizeInfo> {
    return this.ctx.call({ httpMethod: 'GET', path: '/oauth/authorize/info' }, { query: params })
  }

  /** Answers `{ redirectTo, connectionId }`: the client's redirect with the code. */
  async consent(params: AuthorizeParams, communityId: string): Promise<{ redirectTo: string; connectionId: string }> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/oauth/authorize/consent', hasBody: true },
      { body: { ...params, communityId } }
    )
  }

  /** Answers `{ redirectTo }` with `error=access_denied`. */
  async deny(params: AuthorizeParams): Promise<{ redirectTo: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/oauth/authorize/deny', hasBody: true }, { body: params })
  }

  /** The person's own connections, or (`community`, owner/admin) every one in the X-CommunityId community. */
  async connections(scope?: 'community'): Promise<McpConnection[]> {
    return listItems(
      await this.ctx.raw({ httpMethod: 'GET', path: '/oauth/connections' }, scope ? { query: { scope } } : {})
    )
  }

  async revoke(id: string): Promise<{ id: string; revoked: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/oauth/connections/{id}' }, { id })
  }
}
